import type React from 'react';

import type { IconName } from '#ui/index.js';

import { useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';
import { assert } from '@elara/core/errors';

import { CopyButton } from '#ui/copy-button.jsx';
import { IconSizes, MaterialIcon } from '#ui/index.js';

export interface CollapsibleBlockProps {
  icon?: IconName;
  Icon?: React.ComponentType<React.HTMLAttributes<HTMLSpanElement>>;
  children: React.ReactNode;
  label: string | React.ReactNode;
  className?: string;
  initialOpen?: boolean;
  labelClassName?: string;
  align?: 'start' | 'end';
  onCopy?: () => string;
}

export function CollapsibleBlock({
  icon,
  Icon,
  children,
  label,
  className,
  initialOpen,
  labelClassName,
  align = 'start',
  onCopy,
}: CollapsibleBlockProps) {
  assert(icon === undefined || Icon === undefined, 'Provide either `icon` or `Icon` prop, not both');
  const [isOpen, setIsOpen] = useState(initialOpen ?? false);
  const iconClass = 'text-muted-foreground';
  let iconEl = icon ? <MaterialIcon icon={icon} size={IconSizes.xs} className={iconClass} /> : undefined;
  iconEl ??= Icon ? <Icon /> : undefined;
  iconEl ??= <MaterialIcon icon="expand_more" size={IconSizes.xs} className={iconClass} />;

  return (
    <div className={cn('text-xs space-y-1 bg-white/50 p-1 pt-2 my-1 border rounded-md w-full', className)}>
      <div
        role="button"
        tabIndex={0}
        className={cn('mb-1 px-2 cursor-pointer underline-offset-4 hover:text-primary', labelClassName)}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={cn('flex items-center w-full gap-2', align === 'start' ? 'justify-start' : 'justify-end')}>
          {iconEl}
          {typeof label === 'string' ? <div className="font-medium">{label}</div> : label}
          {onCopy && <CopyButton getContent={() => onCopy()} size={'sm'} variant={'ghost'} className="ml-auto" />}
        </div>
      </div>
      {isOpen && (
        <div className={'transition-all duration-300 ease-in-out p-2 m-2 rounded-md  bg-white'}>{children}</div>
      )}
    </div>
  );
}
